import React, { useState, useCallback, useMemo, useEffect, useContext } from "react";

import { first, isNull, noop, set } from "lodash";
import dynamic from "next/dynamic";
import { useParams, usePathname, useRouter, useSearchParams } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { Inventory } from "~/bff/types/Inventory";
import { Store } from "~/bff/types/Store";
import {
  DataLayerData,
  EventsNames,
  PlaceApiProvider,
  StoreSelectorType,
} from "~/constants/data-layer";
import { EventNames as NREventNames } from "~/constants/new-relic";
import { Variables } from "~/constants/request";
import { AuthContext } from "~/context/auth/context";
import { ProductDetailsContext } from "~/context/product-details/context";
import { ShoppingBagContext } from "~/context/shopping-bag/context";
import { StoreSelectorContext } from "~/context/store-selector/context";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { getCountry } from "~/helpers/country/get-country";
import { importComponentByName } from "~/helpers/import-component-by-name";
import { pushDataLayer } from "~/helpers/push-data-layer";
import { setStoreToCookies } from "~/helpers/selected-store/set-store-to-cookies";
import { updateSelectedStoreToAccount } from "~/helpers/selected-store/update-selected-store-to-account";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { useIsEnabled as useIsLoqateEnabled } from "~/hooks/loqate/use-is-enabled";
import { useToken } from "~/hooks/use-token";
import { useUserAccount } from "~/hooks/use-user-account";
import { Nullable } from "~/types/general.types";

import type {
  SelectStorePanelProps,
  SelectStorePanelTranslations,
} from "./components/select-store-panel/component";
import { AvailabilityStatusKey } from "./components/select-store-panel/helpers";
import type { StoreDetailsContentProps } from "./components/store-details-content/component";
import { StoreDetailsContentTranslations } from "./components/store-details-content/types";
import type { StoreSelectorContentProps } from "./components/store-selector-content/types";
import { STORE_SELECTOR_CLASSES, STORE_SELECTOR_TITLE_ID } from "./constants";
import { useGaEvents } from "./hooks/use-ga-events";
import { StoreSelectorDrawer } from "./styled";
import {
  OpenStoreDetailsFunc,
  StoreSelectorPropsExtended,
  StoreSelectorUseType,
} from "./types";

const StoreDetailsContent = dynamic<StoreDetailsContentProps>(async () =>
  importComponentByName(
    import("./components/store-details-content/component"),
    "StoreDetailsContent",
  ),
);
const StoreSelectorContent = dynamic<StoreSelectorContentProps>(async () =>
  importComponentByName(
    import("./components/store-selector-content/component"),
    "StoreSelectorContent",
  ),
);
const SelectStorePanel = dynamic<SelectStorePanelProps>(async () =>
  importComponentByName(
    import("./components/select-store-panel/component"),
    "SelectStorePanel",
  ),
);

export const StoreSelector: React.FC<StoreSelectorPropsExtended> = ({
  children,
  disclaimer,
  productInfo,
  currentStore,
  onCurrentStoreChange,
  onAfterCurrentStoreChange = noop,
  storesInStoreSelector,
  currentStatus,
  currentStyleInventoryInfo,
  onStoresChange,
  offset,
  mainProps,
  onOffsetChange,
  updateVariants = noop,
  updateIsSoldOut,
  type = StoreSelectorUseType.default,
  dataLayerDataState,
  onDataLayerDataChange,
  breadcrumbs,
  fetchInventory,
  styleCode,
}) => {
  const searchParams = useSearchParams();

  const router = useRouter();
  const pathname = usePathname();
  const { handleUpdateShoppingBagLocation } = useContext(ShoppingBagContext) ?? {};
  const { sendOpenStoreSelectorGAEvent, getStoreSearchGAEventData } = useGaEvents();
  const duration = useMemo(() => ({ enteringScreen: 300, leavingScreen: 300 }), []);
  const { locale } = useParams<GlobalPathParams>();
  const country = getCountry(locale);
  const isShoppingListById = useVariable<boolean>(Variables.SHOPPING_LIST_BY_ID);
  const { account, updateAccount } = useContext(AuthContext) ?? {};
  const { extractToken } = useToken(locale);
  const { updateUserAccount } = useUserAccount(locale);
  const { getInventoryByStore } = useContext(ProductDetailsContext) ?? {};
  const isLoqateSearchEnabled = useIsLoqateEnabled();
  const { isSelectorOpenExt, setIsSelectorOpenExt } =
    useContext(StoreSelectorContext) ?? {};

  const selectStorePanelTranslations: SelectStorePanelTranslations = {
    buttonLabel: mainProps?.translations?.selectStoreButtonName ?? undefined,
    storeSelectorStatusesText:
      mainProps?.translations?.storeSelectorProductStatusesTexts ?? undefined,
    storeDetailsStatusesText:
      mainProps?.translations?.storeDetailsProductStatusesTexts ?? undefined,
    oneSizeStoreDetailsProductStatusesTexts:
      mainProps?.translations?.oneSizeProduct
        ?.oneSizeStoreDetailsProductStatusesTexts,
    oneSizeStoreSelectorProductStatusesTexts:
      mainProps?.translations?.oneSizeProduct
        ?.oneSizeStoreSelectorProductStatusesTexts,
    stockAvailabilityStatuses:
      mainProps?.translations?.stockAvailabilityStatuses ?? undefined,
  };

  const storeDetailsContentTranslations: StoreDetailsContentTranslations = {
    backButtonText: mainProps?.translations?.backButtonText,
  };

  const [isStoreSelectorOpen, setIsStoreSelectorOpen] = useState<boolean>(false);

  const [isStoreDetailsOpen, setIsStoreDetailsOpen] = useState<boolean>(false);

  const [count, setCount] = useState(0);
  const onCountChange = useCallback((count: number) => setCount(count), []);

  const [searchValue, setSearchValue] = useState<string>("");
  const [searchWasMade, setSearchWasMade] = useState<boolean>(false);
  const [storeFilter, setStoreFilter] = useState<string>("");
  const [disableBackButton, setDisableBackButton] = useState<boolean>(false);
  const isDrawerOpen =
    isStoreSelectorOpen || isStoreDetailsOpen || isSelectorOpenExt;

  const toggleStoreDetails = useCallback(() => {
    setIsStoreDetailsOpen(!isStoreDetailsOpen);

    // when store-details view is closing
    // we need to reinitialize SearchField-related values
    // as SearchField component remounts, but StoreSelector - doesn't
    if (isStoreDetailsOpen) {
      setSearchValue("");
      setSearchWasMade(false);
    }
  }, [isStoreDetailsOpen]);

  const toggleStoreSelector = useCallback(
    (event?: {
      [DataLayerData.event]: EventsNames;
      [DataLayerData.storeSelectorType]?: StoreSelectorType;
    }) => {
      if (!isStoreSelectorOpen && !isSelectorOpenExt) {
        currentStore && setCheckedStoreCard(currentStore);
        setIsStoreSelectorOpen(true);
        sendOpenStoreSelectorGAEvent(breadcrumbs ?? [], event);

        // when store-selector is opening
        // we need to reinitialize SearchField-related values
        // as SearchField component remounts, but StoreSelector - doesn't
        setSearchValue("");
        setSearchWasMade(false);
      } else {
        setIsStoreSelectorOpen(false);
      }
      setIsSelectorOpenExt?.(false);
      setDisableBackButton(false);
      setIsStoreDetailsOpen(false);
    },
    [
      breadcrumbs,
      currentStore,
      isStoreSelectorOpen,
      sendOpenStoreSelectorGAEvent,
      isSelectorOpenExt,
      setIsSelectorOpenExt,
    ],
  );

  const toggleStoreDetailsFromSalesPanel = useCallback<OpenStoreDetailsFunc>(
    (options) => {
      const { shouldOpenStoreSelector = true, disableBackButton = false } =
        options ?? {};
      setDisableBackButton(disableBackButton);
      setIsStoreSelectorOpen(shouldOpenStoreSelector);
      setIsStoreDetailsOpen(true);
    },
    [],
  );

  const [checkedStoreCard, setCheckedStoreCard] = useState<Nullable<Store>>(
    currentStore ?? null,
  );

  const checkStoreCard = useCallback((store: Nullable<Store>) => {
    setCheckedStoreCard(store);
  }, []);

  useEffect(() => {
    if (!checkedStoreCard) {
      currentStore && setCheckedStoreCard(currentStore);
    }
  }, [checkedStoreCard, currentStore]);

  const pushGaDataToDataLayer = useCallback(
    (storeSelected?: Nullable<Store>): void => {
      const isPdpPage = pathname.startsWith(`/${locale}/p/`);
      const dataForGoogleAnalytics = {
        ...(isPdpPage
          ? {
              ...dataLayerDataState,
              [DataLayerData.searchInput]: searchWasMade
                ? searchValue || undefined
                : undefined,
              [DataLayerData.searchInputCount]: searchWasMade
                ? searchValue?.length || undefined
                : undefined,
              [DataLayerData.storeSelectorType]: productInfo?.isCoords
                ? StoreSelectorType.shopSet
                : StoreSelectorType.pdp,
            }
          : {
              ...getStoreSearchGAEventData(breadcrumbs ?? []),
              [DataLayerData.storeSearch]: searchValue,
              [DataLayerData.storeFilters]: storeFilter,
            }),
        [DataLayerData.event]: EventsNames.storeSelected,
        [DataLayerData.storeSelected]: storeSelected?.geomodifier ?? undefined,
        [DataLayerData.source]: isLoqateSearchEnabled
          ? PlaceApiProvider.loqate
          : PlaceApiProvider.google,
      };
      pushDataLayer({ ...dataForGoogleAnalytics });
      // eslint-disable-next-line no-restricted-globals
      window?.newrelic?.addPageAction(NREventNames.checkAvailability, {
        ...dataForGoogleAnalytics,
        locale,
        country: country?.code2,
      });

      const hasSearchBeenMade = !!dataLayerDataState?.[DataLayerData.storeSelected];
      if (hasSearchBeenMade) {
        onDataLayerDataChange?.({
          ...dataForGoogleAnalytics,
          [DataLayerData.storeSelected]: undefined,
          [DataLayerData.storeSearch]: undefined,
        });
      }
    },
    [
      pathname,
      dataLayerDataState,
      searchWasMade,
      searchValue,
      productInfo?.isCoords,
      getStoreSearchGAEventData,
      breadcrumbs,
      storeFilter,
      isLoqateSearchEnabled,
      locale,
      country?.code2,
      onDataLayerDataChange,
    ],
  );

  const removeDistanceFromStoresBeforeAction = useCallback(
    (callback: () => void) => {
      if (
        first(storesInStoreSelector) &&
        !isNull(first(storesInStoreSelector)?.distance)
      ) {
        const storeWithoutDistance =
          storesInStoreSelector?.map(
            (item) => item && set(item, "distance", null),
          ) ?? [];
        onStoresChange?.(storeWithoutDistance);
      }
      callback();
    },
    [onStoresChange, storesInStoreSelector],
  );

  const updateAccountStore = useCallback(
    async (storeId?: string) => {
      if (account && storeId) {
        updateAccount?.({ ...account, selectedStore: storeId });
      }
    },
    [updateAccount, account],
  );

  const onSelectStore = useCallback(async () => {
    onCurrentStoreChange?.(checkedStoreCard);
    pushGaDataToDataLayer(checkedStoreCard);
    const storeId = checkedStoreCard?.id;
    if (isShoppingListById) {
      const newSearchParams = new URLSearchParams(searchParams.toString());

      if (typeof storeId === "string") {
        newSearchParams.set("store", storeId);
      }

      router.replace(`${pathname}?${newSearchParams.toString()}`);
    }

    if (type === StoreSelectorUseType.default) {
      checkedStoreCard &&
        (await getInventoryByStore?.(checkedStoreCard).then(
          (styleInventoryInStore) => {
            styleInventoryInStore?.soldOut &&
              updateIsSoldOut?.(styleInventoryInStore?.soldOut);
            const colorsAvailable = styleInventoryInStore?.data as Inventory[];

            updateVariants(colorsAvailable, productInfo?.variants, storeId);
          },
        ));
    }

    if (type === StoreSelectorUseType.coordsProduct) {
      checkedStoreCard &&
        typeof styleCode === "string" &&
        fetchInventory?.(checkedStoreCard, styleCode);
    }

    if (
      type === StoreSelectorUseType.coordsProduct &&
      checkedStoreCard &&
      typeof styleCode === "string"
    ) {
      fetchInventory?.(checkedStoreCard, styleCode);
    }

    if (type === StoreSelectorUseType.coordsProduct) {
      fetchInventory?.(checkedStoreCard, styleCode);
    }

    if (type === StoreSelectorUseType.coordsProduct) {
      fetchInventory?.(checkedStoreCard, styleCode);
    }

    onAfterCurrentStoreChange(checkedStoreCard);
    if (account) {
      const token = await extractToken();
      await updateSelectedStoreToAccount(account, storeId, token, updateUserAccount);
    } else {
      typeof storeId === "string" && setStoreToCookies(storeId);
    }

    typeof storeId === "string" &&
      (await handleUpdateShoppingBagLocation?.(storeId));

    updateAccountStore(storeId);

    removeDistanceFromStoresBeforeAction(toggleStoreSelector);
  }, [
    onCurrentStoreChange,
    checkedStoreCard,
    pushGaDataToDataLayer,
    isShoppingListById,
    type,
    onAfterCurrentStoreChange,
    handleUpdateShoppingBagLocation,
    account,
    updateAccountStore,
    removeDistanceFromStoresBeforeAction,
    toggleStoreSelector,
    searchParams,
    router,
    pathname,
    getInventoryByStore,
    updateIsSoldOut,
    updateVariants,
    productInfo?.variants,
    fetchInventory,
    styleCode,
    extractToken,
    updateUserAccount,
  ]);

  const isCurrentStoreNotEqualsCheckedStore = useMemo(
    () => checkedStoreCard?.id !== currentStore?.id,
    [checkedStoreCard?.id, currentStore?.id],
  );

  const showSelectStorePanel = useMemo(
    () => checkedStoreCard && isCurrentStoreNotEqualsCheckedStore,
    [checkedStoreCard, isCurrentStoreNotEqualsCheckedStore],
  );

  const currentSize = useMemo(
    () =>
      productInfo?.variants?.find((size) => size?.sku === productInfo?.sku)
        ?.displaySize,
    [productInfo?.sku, productInfo?.variants],
  );

  return (
    <>
      {children({
        openStoreSelector: toggleStoreSelector,
        openStoreDetails: toggleStoreDetailsFromSalesPanel,
        setCheckedStoreCard: setCheckedStoreCard,
      })}
      <StoreSelectorDrawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => toggleStoreSelector()}
        transitionDuration={{
          enter: duration.enteringScreen,
          exit: duration.leavingScreen,
        }}
        classes={{ paper: STORE_SELECTOR_CLASSES.drawerContent }}
        PaperProps={{
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": STORE_SELECTOR_TITLE_ID,
          ...getTestAutomationProps("slide-panel"),
        }}
      >
        {isStoreDetailsOpen ? (
          <StoreDetailsContent
            country={country ?? undefined}
            currentStore={checkedStoreCard ?? undefined}
            toggleStoreDetails={toggleStoreDetails}
            toggleStoreSelector={toggleStoreSelector}
            disableBackButton={disableBackButton}
            translations={storeDetailsContentTranslations}
            productId={productInfo?.productId}
            productName={productInfo?.productName}
            breadcrumbs={breadcrumbs}
          />
        ) : (
          <StoreSelectorContent
            mainProps={mainProps}
            disclaimer={disclaimer}
            currentStore={checkedStoreCard}
            checkStore={checkStoreCard}
            toggleStoreDetails={toggleStoreDetails}
            toggleStoreSelector={() =>
              removeDistanceFromStoresBeforeAction(toggleStoreSelector)
            }
            storesInStoreSelector={storesInStoreSelector ?? []}
            country={country ?? undefined}
            currentStatus={currentStatus}
            currentStyleInventoryInfo={currentStyleInventoryInfo}
            storeFromPdP={currentStore}
            onStoresChange={onStoresChange}
            offset={offset}
            onOffsetChange={onOffsetChange}
            count={count}
            onCountChange={onCountChange}
            sku={productInfo?.sku}
            isCoords={productInfo?.isCoords}
            salesChannel={productInfo?.salesChannel}
            type={type}
            dataLayerDataState={
              dataLayerDataState || getStoreSearchGAEventData(breadcrumbs ?? [])
            }
            onDataLayerDataChange={onDataLayerDataChange}
            onSearch={() => setSearchWasMade(true)}
            onSearchValueChange={(value) => setSearchValue(value)}
            onStoreFilterChange={(value) => setStoreFilter(value)}
          />
        )}
        {showSelectStorePanel && (
          <SelectStorePanel
            isOneSize={productInfo?.isOneSize}
            isStoreDetailsOpen={isStoreDetailsOpen}
            onSelectStore={onSelectStore}
            size={currentSize ?? undefined}
            statusKey={
              (checkedStoreCard?.inventory?.available as AvailabilityStatusKey) ??
              undefined
            }
            type={type}
            translations={selectStorePanelTranslations}
          />
        )}
      </StoreSelectorDrawer>
    </>
  );
};
